// src/lib/duckdb/context.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from "react";
import { useDuckDBConnection } from "./init";
import { listRelations } from "./catalog";

interface DatabaseCtxValue {
  db: any;
  conn: any;
  loading: boolean;
  error: Error | null;
  tables: string[];
  refreshTables: () => Promise<void>;
}

const DatabaseContext = createContext<DatabaseCtxValue | undefined>(
  undefined
);

export const DatabaseProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { db, conn, loading, error } = useDuckDBConnection();
  const [tables, setTables] = useState<string[]>([]);

  const refreshTables = async () => {
    if (!conn) {
      setTables([]);
      return;
    }
    try {
      const names = await listRelations(conn);
      setTables(names);
    } catch {
      setTables([]);
    }
  };

  useEffect(() => {
    if (conn) refreshTables();
    else setTables([]);
  }, [conn]);

  const value = useMemo(
    () => ({ db, conn, loading, error, tables, refreshTables }),
    [db, conn, loading, error, tables]
  );

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

export function useDatabase() {
  const ctx = useContext(DatabaseContext);
  if (!ctx)
    throw new Error("useDatabase must be used inside <DatabaseProvider>");
  return ctx;
}
